import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { creatorApi } from '../../api/creatorApi';
import { followListApi } from '../../api/followListApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import SafeImage from '../../components/SafeImage';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { travelMemberProductDisplayName } from '../member-identity/memberIdentityPresentation';
import {
  creatorPath,
  normalizeConnectionPage,
} from './creatorConnectionsModel';
import './creator-connections.css';

/**
 * Shared Followers / Following people list (backend-hydrated).
 * mode: 'followers' | 'following'
 */
export default function CreatorConnectionsPage({ mode }) {
  const { memberId } = useParams();
  const subjectId = memberId;
  const { t } = useTravelTranslation();
  const generation = useRef(0);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [subjectName, setSubjectName] = useState('');

  const isFollowers = mode === 'followers';
  const title = isFollowers ? t('travel.creator.followersTitle') : t('travel.creator.followingPeopleTitle');
  const emptyTitle = isFollowers ? t('travel.creator.followersEmptyTitle') : t('travel.creator.followingPeopleEmptyTitle');
  const emptyHint = isFollowers ? t('travel.creator.followersEmptyHint') : t('travel.creator.followingPeopleEmptyHint');

  const load = useCallback(async ({ append = false, cursor = null } = {}) => {
    const requestGeneration = generation.current;
    setPhase(append ? 'loading-more' : 'loading');
    setError('');
    try {
      if (!/^[1-9]\d*$/.test(subjectId || '')) {
        setPhase('not-found');
        return;
      }
      const params = { limit: 20 };
      if (cursor != null && cursor !== '') params.cursorFollowId = cursor;
      const raw = isFollowers
        ? await followListApi.listFollowers(subjectId, params)
        : await followListApi.listFollowing(subjectId, params);
      if (requestGeneration !== generation.current) return;
      const page = normalizeConnectionPage(raw);
      setItems((current) => (append ? [...current, ...page.items] : page.items));
      setNextCursor(page.nextCursorFollowId);
      setHasMore(page.hasMore);
      setPhase('ready');
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      const notFound = cause?.raw?.key === 'travel.social.creator.not-found'
        || cause?.raw?.code === 96300
        || cause?.raw?.status === 404
        || cause?.response?.status === 404;
      if (notFound && !append) {
        setPhase('not-found');
        return;
      }
      setError(resolveSafeUserVisibleError(cause, t, 'travel.creator.connectionsLoadFailed'));
      setPhase(append ? 'ready' : 'error');
    }
  }, [isFollowers, subjectId, t]);

  useEffect(() => {
    generation.current += 1;
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    setSubjectName('');
    load();
  }, [load]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const profile = await creatorApi.getProfile(subjectId);
        if (!active) return;
        const name = travelMemberProductDisplayName({
          displayName: profile?.displayName,
          username: profile?.username,
          nickname: profile?.nickname,
        });
        if (name) setSubjectName(name);
      } catch {
        /* optional subject label */
      }
    })();
    return () => { active = false; };
  }, [subjectId]);

  if (phase === 'loading' && items.length === 0) {
    return (
      <main className="creator-connections" aria-live="polite">
        <div className="creator-connections__state">
          <div className="explore-spinner" />
          <p>{t('travel.creator.connectionsLoading')}</p>
        </div>
      </main>
    );
  }

  if (phase === 'not-found') {
    return (
      <main className="creator-connections">
        <div className="creator-connections__state">
          <h1>{t('travel.creator.unavailableTitle')}</h1>
          <p>{t('travel.creator.unavailableHint')}</p>
          <Link to="/explore">{t('travel.creator.backToExplore')}</Link>
        </div>
      </main>
    );
  }

  if (phase === 'error' && items.length === 0) {
    return (
      <main className="creator-connections" role="alert">
        <div className="creator-connections__state">
          <h1>{t('travel.creator.connectionsLoadFailedTitle')}</h1>
          <p>{error}</p>
          <button type="button" onClick={() => load()}>{t('travel.creator.tryAgain')}</button>
        </div>
      </main>
    );
  }

  const backTo = creatorPath(subjectId) || '/explore';

  return (
    <main className="creator-connections" aria-labelledby="creator-connections-title">
      <header className="creator-connections__header">
        <Link to={backTo} className="creator-connections__back">{t('travel.common.back')}</Link>
        <div>
          <p className="creator-connections__eyebrow">{subjectName || t('travel.creator.eyebrow')}</p>
          <h1 id="creator-connections-title">{title}</h1>
        </div>
      </header>

      {items.length === 0 ? (
        <section className="creator-connections__state">
          <h2>{emptyTitle}</h2>
          <p>{emptyHint}</p>
        </section>
      ) : (
        <ul className="creator-connections__list">
          {items.map((item) => {
            const to = creatorPath(item.memberId);
            if (!to) return null;
            return (
              <li key={item.memberId}>
                <Link
                  to={to}
                  className="creator-connections__item"
                  aria-label={`${t('travel.creator.openCreatorProfile')}: ${item.displayName}`}
                >
                  <SafeImage
                    src={item.avatarUrl}
                    alt=""
                    fallback={(
                      <span className="creator-connections__avatar" aria-hidden="true">
                        {(item.displayName || '?').slice(0, 1).toUpperCase()}
                      </span>
                    )}
                  />
                  <span className="creator-connections__name">{item.displayName}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {error && items.length > 0 ? (
        <p className="creator-connections__inline-error" role="alert">{error}</p>
      ) : null}

      {hasMore ? (
        <div className="creator-connections__more">
          <button
            type="button"
            disabled={phase === 'loading-more'}
            onClick={() => load({ append: true, cursor: nextCursor })}
          >
            {phase === 'loading-more' ? t('travel.common.loading') : t('travel.common.loadMore')}
          </button>
        </div>
      ) : null}
    </main>
  );
}
